import React, { useState } from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from 'recharts';
import {
  Store,
  CheckCircle2,
  Handshake,
  Users,
  Megaphone,
  ShieldAlert,
  DollarSign,
  CreditCard,
} from 'lucide-react';
import { backofficeApi, formatNumber, formatCurrency, formatGrowth } from '@kardume/services';
import { theme } from '@kardume/theme';
import type { MetricsRange } from '@kardume/types';
import { PageHeader, StatCard, Card, CardHeader, CardBody, Select } from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Charts = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const RANGE_OPTIONS = [
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: '12m', label: 'Últimos 12 meses' },
];

const DashboardPage: React.FC = () => {
  const [range, setRange] = useState<MetricsRange>('30d');
  const { data, loading, error, reload } = useAsync(() => backofficeApi.getDashboard(range), [range]);

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Visão geral da plataforma Kardume Urbano."
        actions={
          <Select
            options={RANGE_OPTIONS}
            value={range}
            onChange={(e) => setRange(e.target.value as MetricsRange)}
          />
        }
      />

      <DataState loading={loading} error={error} onRetry={reload}>
        {data && (
          <>
            <Grid>
              <StatCard
                label="Lojistas"
                value={formatNumber(data.totalMerchants)}
                icon={<Store size={18} />}
                iconColor={theme.primary}
                delta={formatGrowth(data.merchantsGrowth)}
                deltaPositive={data.merchantsGrowth >= 0}
              />
              <StatCard
                label="Lojistas ativos"
                value={formatNumber(data.activeMerchants)}
                icon={<CheckCircle2 size={18} />}
                iconColor={theme.success}
              />
              <StatCard
                label="Parceiras"
                value={formatNumber(data.partnerMerchants ?? 0)}
                icon={<Handshake size={18} />}
                iconColor={theme.info}
              />
              <StatCard
                label="Usuários"
                value={formatNumber(data.totalCustomers)}
                icon={<Users size={18} />}
                iconColor={theme.info}
                delta={formatGrowth(data.customersGrowth)}
                deltaPositive={data.customersGrowth >= 0}
              />
              <StatCard
                label="Anúncios ativos"
                value={formatNumber(data.activePromotions)}
                icon={<Megaphone size={18} />}
                iconColor={theme.accent}
              />
              <StatCard
                label="Aguardando moderação"
                value={formatNumber(data.pendingModeration)}
                icon={<ShieldAlert size={18} />}
                iconColor={theme.warning}
              />
              <StatCard
                label="Receita recorrente (MRR)"
                value={formatCurrency(data.mrr)}
                icon={<DollarSign size={18} />}
                iconColor={theme.primary}
                delta={formatGrowth(data.mrrGrowth)}
                deltaPositive={data.mrrGrowth >= 0}
              />
              <StatCard
                label="Assinaturas ativas"
                value={formatNumber(data.activeSubscriptions)}
                icon={<CreditCard size={18} />}
                iconColor={theme.info}
              />
            </Grid>

            <Charts>
              <Card>
                <CardHeader>
                  <h3>Novos lojistas x Receita</h3>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={mergeSeries(data.merchantsSeries, data.revenueSeries)}>
                      <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.primary} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={theme.primary} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.accent} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={theme.accent} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: theme.textMuted }} />
                      <YAxis tick={{ fontSize: 12, fill: theme.textMuted }} />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="merchants"
                        name="Lojistas"
                        stroke={theme.primary}
                        fill="url(#g1)"
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        name="Receita"
                        stroke={theme.accent}
                        fill="url(#g2)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h3>Lojistas por categoria</h3>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={data.merchantsByCategory} layout="vertical" margin={{ left: 12 }}>
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={110}
                        tick={{ fontSize: 12, fill: theme.textSecondary }}
                      />
                      <Tooltip />
                      <Bar dataKey="value" name="Lojistas" fill={theme.primary} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </Charts>
          </>
        )}
      </DataState>
    </>
  );
};

/** Merge the merchants and revenue time series into a single dataset keyed by date. */
function mergeSeries(
  merchants: { date: string; value: number }[],
  revenue: { date: string; value: number }[]
) {
  const map = new Map<string, { date: string; merchants: number; revenue: number }>();
  merchants.forEach((p) => map.set(p.date, { date: p.date, merchants: p.value, revenue: 0 }));
  revenue.forEach((p) => {
    const existing = map.get(p.date);
    if (existing) existing.revenue = p.value;
    else map.set(p.date, { date: p.date, merchants: 0, revenue: p.value });
  });
  return Array.from(map.values());
}

export default DashboardPage;
